import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BILLING_CURRENCIES,
  CURRENCY_META,
  formatMinor,
  type BillingCurrency,
  type CouponUpsert,
  type BillingCurrency as Cur,
  type PlanUpsert,
  type TaxSetting,
} from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { Modal, Switch } from "./adminUi.js";
import { serverMessage } from "../users/planUsage.js";

/**
 * Owner console · Plans & coupons (0094). The customer-facing catalog — plans (jobs / resumes /
 * AI interviews for a price), coupons, and the tax setting. Nothing is hardcoded: Starter /
 * Professional / Enterprise are seed rows the owner can change or retire. A purchase snapshots
 * the plan at checkout, so editing here never changes what an existing customer bought.
 *
 * Amounts are entered in major units (₹) and stored in minor units (paise).
 */

interface AdminPlan extends PlanUpsert {
  id: string;
  sold: { purchases: number; units: number };
}
interface AdminCoupon extends CouponUpsert {
  id: string;
  uses: number;
  discountGivenMinor: number;
}
interface AdminPlansResponse {
  plans: AdminPlan[];
  coupons: AdminCoupon[];
  tax: TaxSetting;
  /** minor units per currency — the "you save vs human interviews" estimate input */
  humanInterviewCost: Partial<Record<Cur, number>>;
}

const KEY = ["admin-plans"] as const;
const toMinor = (major: string, cur: BillingCurrency): number =>
  Math.round(Number(major) * CURRENCY_META[cur].minor);
const toMajor = (minor: number, cur: BillingCurrency): string => String(minor / CURRENCY_META[cur].minor);

const EMPTY_PLAN: PlanUpsert = {
  name: "",
  description: "",
  currency: "INR",
  priceMinor: 0,
  compareAtMinor: null,
  jobs: 1,
  resumes: 0,
  interviews: 0,
  isCustom: false,
  isActive: true,
  isFeatured: false,
  sortOrder: 10,
  validityMonths: 12,
  minQty: 1,
  maxQty: 50,
};

const EMPTY_COUPON: CouponUpsert = {
  code: "",
  description: "",
  discountType: "percent",
  discountValue: 10,
  currency: null,
  maxDiscountMinor: null,
  minQty: null,
  minSubtotalMinor: null,
  startsAt: null,
  expiresAt: null,
  usageLimit: null,
  perOrgLimit: null,
  firstPurchaseOnly: false,
  isActive: true,
  planIds: [],
};

export function PlansAdmin(): ReactNode {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api.get<AdminPlansResponse>("/admin/plans") });
  const [planEdit, setPlanEdit] = useState<{ id: string | null; v: PlanUpsert } | null>(null);
  const [couponEdit, setCouponEdit] = useState<{ id: string | null; v: CouponUpsert } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: KEY });

  const savePlan = useMutation({
    mutationFn: (x: { id: string | null; v: PlanUpsert }) =>
      x.id ? api.put(`/admin/plans/${x.id}`, x.v) : api.post("/admin/plans", x.v),
    onSuccess: () => {
      setPlanEdit(null);
      refresh();
    },
  });
  const saveCoupon = useMutation({
    mutationFn: (x: { id: string | null; v: CouponUpsert }) =>
      x.id ? api.put(`/admin/coupons/${x.id}`, x.v) : api.post("/admin/coupons", x.v),
    onSuccess: () => {
      setCouponEdit(null);
      refresh();
    },
  });
  const saveTax = useMutation({
    mutationFn: (t: TaxSetting) => api.put("/admin/tax", t),
    onSuccess: refresh,
    onError: (e: unknown) => setErr(serverMessage(e, "Couldn't save the tax setting.")),
  });
  const saveHuman = useMutation({
    mutationFn: (v: Partial<Record<Cur, number>>) => api.put("/admin/human-interview-cost", v),
    onSuccess: refresh,
    onError: (e: unknown) => setErr(serverMessage(e, "Couldn't save the human interview cost.")),
  });

  const d = q.data;
  if (q.isLoading || !d) return <section><p className="mut sm">Loading plans…</p></section>;
  const planName = (id: string) => d.plans.find((p) => p.id === id)?.name ?? "?";

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Plans & coupons</h1>
          <div className="sub">
            What customers buy — jobs, resumes and AI interviews, in any quantity, pooled per organisation.
            Checkouts snapshot the plan, so edits never change past purchases.
          </div>
        </div>
      </div>

      {err && <div className="banner crit" style={{ marginBottom: 12 }}><span>{err}</span></div>}

      <TaxCard tax={d.tax} busy={saveTax.isPending} onSave={(t) => { setErr(null); saveTax.mutate(t); }} />
      <HumanCostCard cost={d.humanInterviewCost} busy={saveHuman.isPending}
        onSave={(v) => { setErr(null); saveHuman.mutate(v); }} />

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-h">
          <h3>Plans</h3>
          <button type="button" className="btn sm" onClick={() => { savePlan.reset(); setPlanEdit({ id: null, v: EMPTY_PLAN }); }}>
            New plan
          </button>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Plan</th>
                <th style={{ textAlign: "right" }}>Price</th>
                <th style={{ textAlign: "right" }}>Jobs</th>
                <th style={{ textAlign: "right" }}>Resumes</th>
                <th style={{ textAlign: "right" }}>AI interviews</th>
                <th>Qty</th>
                <th>Validity</th>
                <th>Sold</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.plans.map((p) => (
                <tr key={p.id} style={p.isActive ? undefined : { opacity: 0.55 }}>
                  <td>
                    <b>{p.name}</b>
                    {p.isFeatured && <span className="pill t" style={{ marginLeft: 6 }}>featured</span>}
                    {p.isCustom && <span className="pill t" style={{ marginLeft: 6 }}>custom</span>}
                    {!p.isActive && <span className="pill n" style={{ marginLeft: 6 }}>inactive</span>}
                  </td>
                  <td className="tnum" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {p.isCustom ? "—" : (
                      <>
                        {p.compareAtMinor != null && (
                          <s className="mut" style={{ fontSize: 12, marginRight: 5 }}>{formatMinor(p.compareAtMinor, p.currency)}</s>
                        )}
                        {formatMinor(p.priceMinor, p.currency)}
                      </>
                    )}
                  </td>
                  <td className="tnum" style={{ textAlign: "right" }}>{p.jobs.toLocaleString("en-IN")}</td>
                  <td className="tnum" style={{ textAlign: "right" }}>{p.resumes.toLocaleString("en-IN")}</td>
                  <td className="tnum" style={{ textAlign: "right" }}>{p.interviews.toLocaleString("en-IN")}</td>
                  <td className="tnum mut sm">{p.minQty}–{p.maxQty}</td>
                  <td className="mut sm">{p.validityMonths} mo</td>
                  <td className="tnum mut sm">{p.sold.purchases} · ×{p.sold.units}</td>
                  <td style={{ textAlign: "right" }}>
                    <button type="button" className="btn sm ghost" onClick={() => {
                      savePlan.reset();
                      const { id, sold: _sold, ...v } = p;
                      setPlanEdit({ id, v });
                    }}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-h">
          <h3>Coupons</h3>
          <button type="button" className="btn sm" onClick={() => { saveCoupon.reset(); setCouponEdit({ id: null, v: EMPTY_COUPON }); }}>
            New coupon
          </button>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Applies to</th>
                <th>Valid</th>
                <th>Uses</th>
                <th>Given</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.coupons.length === 0 && <tr><td colSpan={7} className="mut sm">No coupons yet.</td></tr>}
              {d.coupons.map((c) => (
                <tr key={c.id} style={c.isActive ? undefined : { opacity: 0.55 }}>
                  <td>
                    <b className="tnum">{c.code}</b>
                    {!c.isActive && <span className="pill n" style={{ marginLeft: 6 }}>inactive</span>}
                    {c.firstPurchaseOnly && <span className="pill n" style={{ marginLeft: 6 }}>first purchase</span>}
                  </td>
                  <td className="tnum">
                    {c.discountType === "percent" ? `${c.discountValue}%` : formatMinor(c.discountValue, c.currency ?? "INR")}
                    {c.maxDiscountMinor != null && (
                      <span className="mut sm"> · max {formatMinor(c.maxDiscountMinor, c.currency ?? "INR")}</span>
                    )}
                  </td>
                  <td className="sm">{c.planIds.length === 0 ? "All plans" : c.planIds.map(planName).join(", ")}</td>
                  <td className="mut sm tnum">
                    {c.startsAt ? new Date(c.startsAt).toLocaleDateString() : "now"} –{" "}
                    {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : "no expiry"}
                  </td>
                  <td className="tnum sm">
                    {c.uses}{c.usageLimit != null ? ` / ${c.usageLimit}` : ""}
                    {c.perOrgLimit != null && <span className="mut"> · {c.perOrgLimit}/org</span>}
                  </td>
                  <td className="tnum sm">{formatMinor(c.discountGivenMinor, c.currency ?? "INR")}</td>
                  <td style={{ textAlign: "right" }}>
                    <button type="button" className="btn sm ghost" onClick={() => {
                      saveCoupon.reset();
                      const { id, uses: _u, discountGivenMinor: _g, ...v } = c;
                      setCouponEdit({ id, v });
                    }}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {planEdit && (
        <PlanForm
          init={planEdit.v}
          isNew={!planEdit.id}
          busy={savePlan.isPending}
          error={savePlan.isError ? serverMessage(savePlan.error, "Couldn't save the plan — check the fields.") : null}
          onClose={() => setPlanEdit(null)}
          onSave={(v) => savePlan.mutate({ id: planEdit.id, v })}
        />
      )}
      {couponEdit && (
        <CouponForm
          init={couponEdit.v}
          isNew={!couponEdit.id}
          plans={d.plans}
          busy={saveCoupon.isPending}
          error={saveCoupon.isError ? serverMessage(saveCoupon.error, "Couldn't save the coupon — check the fields.") : null}
          onClose={() => setCouponEdit(null)}
          onSave={(v) => saveCoupon.mutate({ id: couponEdit.id, v })}
        />
      )}
    </section>
  );
}

function TaxCard({ tax, busy, onSave }: { tax: TaxSetting; busy: boolean; onSave: (t: TaxSetting) => void }): ReactNode {
  const [t, setT] = useState<TaxSetting>(tax);
  const dirty = JSON.stringify(t) !== JSON.stringify(tax);
  return (
    <div className="card pad" style={{ marginBottom: 16, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
      <b>Tax</b>
      <Switch on={t.enabled} onToggle={() => setT({ ...t, enabled: !t.enabled })} />
      <input className="input" style={{ width: 80 }} value={t.name} aria-label="Tax name"
        onChange={(e) => setT({ ...t, name: e.target.value })} />
      <input className="input tnum" style={{ width: 70 }} type="number" min={0} max={50} step="0.01" value={t.pct}
        aria-label="Tax percent" onChange={(e) => setT({ ...t, pct: Number(e.target.value) })} />
      <span className="mut sm">%</span>
      <label className="sm" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
        <Switch on={t.inclusive} onToggle={() => setT({ ...t, inclusive: !t.inclusive })} />
        {t.inclusive ? "included in plan prices" : "added on top of plan prices"}
      </label>
      <span className="mut sm">· applies to</span>
      {BILLING_CURRENCIES.map((c) => (
        <label key={c} className="sm" style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
          <input type="checkbox" checked={t.currencies.includes(c)} onChange={(e) =>
            setT({ ...t, currencies: e.target.checked ? [...t.currencies, c] : t.currencies.filter((x) => x !== c) })} />
          {c}
        </label>
      ))}
      <button type="button" className="btn sm" disabled={!dirty || busy} onClick={() => onSave(t)}>
        {busy ? "Saving…" : "Save"}
      </button>
      <span className="mut sm">Partner commission is calculated on amounts excluding this tax.</span>
    </div>
  );
}

function HumanCostCard({
  cost, busy, onSave,
}: { cost: Partial<Record<Cur, number>>; busy: boolean; onSave: (v: Partial<Record<Cur, number>>) => void }): ReactNode {
  const [draft, setDraft] = useState<Record<string, string>>(
    Object.fromEntries(BILLING_CURRENCIES.map((c) => [c, cost[c] != null ? toMajor(cost[c]!, c) : ""])),
  );
  const next = (): Partial<Record<Cur, number>> =>
    Object.fromEntries(
      BILLING_CURRENCIES.filter((c) => draft[c]?.trim()).map((c) => [c, toMinor(draft[c]!, c)]),
    ) as Partial<Record<Cur, number>>;
  const dirty = JSON.stringify(next()) !== JSON.stringify(cost);
  return (
    <div className="card pad" style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <b>Human interview cost</b>
      {BILLING_CURRENCIES.map((c) => (
        <label key={c} className="sm" style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
          {CURRENCY_META[c].symbol}
          <input className="input tnum" style={{ width: 90 }} type="number" min={0} placeholder="—" value={draft[c]}
            aria-label={`Human interview cost ${c}`} onChange={(e) => setDraft({ ...draft, [c]: e.target.value })} />
        </label>
      ))}
      <button type="button" className="btn sm" disabled={!dirty || busy} onClick={() => onSave(next())}>
        {busy ? "Saving…" : "Save"}
      </button>
      <span className="mut sm">Per interview. Drives the customer's "save vs human interviews" estimate; blank hides it.</span>
    </div>
  );
}

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }): ReactNode {
  return (
    <div className="field" style={{ marginBottom: 10 }}>
      <label>{label}</label>
      {children}
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

const two: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
const three: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 };

function PlanForm({
  init, isNew, busy, error, onClose, onSave,
}: {
  init: PlanUpsert; isNew: boolean; busy: boolean; error: string | null;
  onClose: () => void; onSave: (v: PlanUpsert) => void;
}): ReactNode {
  const [v, setV] = useState<PlanUpsert>(init);
  const [price, setPrice] = useState(toMajor(init.priceMinor, init.currency));
  const [was, setWas] = useState(init.compareAtMinor != null ? toMajor(init.compareAtMinor, init.currency) : "");
  const num = (k: keyof PlanUpsert) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV({ ...v, [k]: Math.floor(Number(e.target.value)) });
  const submit = () =>
    onSave({
      ...v,
      description: v.description || null,
      priceMinor: toMinor(price || "0", v.currency),
      compareAtMinor: was.trim() && !v.isCustom ? toMinor(was, v.currency) : null,
    });
  return (
    <Modal
      title={isNew ? "New plan" : `Edit ${init.name}`}
      onClose={onClose}
      maxWidth={560}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn" disabled={busy || !v.name.trim()} onClick={submit}>
            {busy ? "Saving…" : "Save plan"}
          </button>
        </div>
      }
    >
      {error && <div className="banner crit" style={{ marginBottom: 10 }}><span>{error}</span></div>}
      <div style={two}>
        <Field label="Name"><input className="input" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></Field>
        <Field label="Description"><input className="input" value={v.description ?? ""} onChange={(e) => setV({ ...v, description: e.target.value })} /></Field>
      </div>
      <div style={three}>
        <Field label="Currency">
          <select className="input" value={v.currency} onChange={(e) => setV({ ...v, currency: e.target.value as BillingCurrency })}>
            {BILLING_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label={`Price (${CURRENCY_META[v.currency].symbol})`} hint="as the customer pays it (see Tax)">
          <input className="input tnum" type="number" min={0} step="0.01" value={price} disabled={v.isCustom}
            onChange={(e) => setPrice(e.target.value)} />
        </Field>
        <Field label={`Strike-through price (${CURRENCY_META[v.currency].symbol})`} hint="shown crossed out; blank = none">
          <input className="input tnum" type="number" min={0} step="0.01" value={was} disabled={v.isCustom}
            onChange={(e) => setWas(e.target.value)} />
        </Field>
      </div>
      <div style={three}>
        <Field label="Validity (months)"><input className="input tnum" type="number" min={1} max={36} value={v.validityMonths} onChange={num("validityMonths")} /></Field>
      </div>
      <div style={three}>
        <Field label="Jobs"><input className="input tnum" type="number" min={0} value={v.jobs} onChange={num("jobs")} /></Field>
        <Field label="Resumes"><input className="input tnum" type="number" min={0} value={v.resumes} onChange={num("resumes")} /></Field>
        <Field label="AI interviews"><input className="input tnum" type="number" min={0} value={v.interviews} onChange={num("interviews")} /></Field>
      </div>
      <div style={three}>
        <Field label="Min quantity"><input className="input tnum" type="number" min={1} value={v.minQty} onChange={num("minQty")} /></Field>
        <Field label="Max quantity"><input className="input tnum" type="number" min={1} value={v.maxQty} onChange={num("maxQty")} /></Field>
        <Field label="Display order"><input className="input tnum" type="number" min={0} value={v.sortOrder} onChange={num("sortOrder")} /></Field>
      </div>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 4 }}>
        {([
          ["isActive", "Active"],
          ["isFeatured", "Recommended"],
          ["isCustom", "Custom (contact us, no checkout)"],
        ] as const).map(([k, label]) => (
          <label key={k} className="sm" style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <Switch on={v[k]} onToggle={() => setV({ ...v, [k]: !v[k] })} /> {label}
          </label>
        ))}
      </div>
      <p className="mut sm" style={{ marginBottom: 0 }}>Billing: one-time purchase. Allowances are multiplied by the quantity bought.</p>
    </Modal>
  );
}

const isoOrNull = (d: string): string | null => (d ? new Date(`${d}T00:00:00`).toISOString() : null);
const dateInput = (iso: string | null): string => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
const intOrNull = (s: string): number | null => (s.trim() === "" ? null : Math.floor(Number(s)));

function CouponForm({
  init, isNew, plans, busy, error, onClose, onSave,
}: {
  init: CouponUpsert; isNew: boolean; plans: AdminPlan[]; busy: boolean; error: string | null;
  onClose: () => void; onSave: (v: CouponUpsert) => void;
}): ReactNode {
  const cur0: BillingCurrency = init.currency ?? "INR";
  const [v, setV] = useState<CouponUpsert>(init);
  const [cur, setCur] = useState<BillingCurrency>(cur0);
  const [value, setValue] = useState(
    init.discountType === "fixed" ? toMajor(init.discountValue, cur0) : String(init.discountValue),
  );
  const [maxDisc, setMaxDisc] = useState(init.maxDiscountMinor != null ? toMajor(init.maxDiscountMinor, cur0) : "");
  const [minSub, setMinSub] = useState(init.minSubtotalMinor != null ? toMajor(init.minSubtotalMinor, cur0) : "");
  const fixed = v.discountType === "fixed";
  const submit = () =>
    onSave({
      ...v,
      description: v.description || null,
      currency: fixed || maxDisc || minSub ? cur : null,
      discountValue: fixed ? toMinor(value || "0", cur) : Number(value),
      maxDiscountMinor: maxDisc ? toMinor(maxDisc, cur) : null,
      minSubtotalMinor: minSub ? toMinor(minSub, cur) : null,
    });
  return (
    <Modal
      title={isNew ? "New coupon" : `Edit ${init.code}`}
      onClose={onClose}
      maxWidth={560}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn" disabled={busy || !v.code.trim() || !value} onClick={submit}>
            {busy ? "Saving…" : "Save coupon"}
          </button>
        </div>
      }
    >
      {error && <div className="banner crit" style={{ marginBottom: 10 }}><span>{error}</span></div>}
      <div style={two}>
        <Field label="Code">
          <input className="input" style={{ textTransform: "uppercase" }} value={v.code}
            onChange={(e) => setV({ ...v, code: e.target.value.toUpperCase() })} />
        </Field>
        <Field label="Description"><input className="input" value={v.description ?? ""} onChange={(e) => setV({ ...v, description: e.target.value })} /></Field>
      </div>
      <div style={three}>
        <Field label="Type">
          <select className="input" value={v.discountType}
            onChange={(e) => setV({ ...v, discountType: e.target.value as "percent" | "fixed" })}>
            <option value="percent">Percentage</option>
            <option value="fixed">Fixed amount</option>
          </select>
        </Field>
        <Field label={fixed ? `Amount (${CURRENCY_META[cur].symbol})` : "Percent"}>
          <input className="input tnum" type="number" min={0} step="0.01" value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
        <Field label="Currency" hint="for amounts">
          <select className="input" value={cur} onChange={(e) => setCur(e.target.value as BillingCurrency)}>
            {BILLING_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>
      <div style={three}>
        <Field label="Max discount"><input className="input tnum" type="number" min={0} value={maxDisc} placeholder="none" onChange={(e) => setMaxDisc(e.target.value)} /></Field>
        <Field label="Min subtotal"><input className="input tnum" type="number" min={0} value={minSub} placeholder="none" onChange={(e) => setMinSub(e.target.value)} /></Field>
        <Field label="Min quantity">
          <input className="input tnum" type="number" min={1} value={v.minQty ?? ""} placeholder="none"
            onChange={(e) => setV({ ...v, minQty: intOrNull(e.target.value) })} />
        </Field>
      </div>
      <div style={two}>
        <Field label="Starts"><input className="input" type="date" value={dateInput(v.startsAt)} onChange={(e) => setV({ ...v, startsAt: isoOrNull(e.target.value) })} /></Field>
        <Field label="Expires"><input className="input" type="date" value={dateInput(v.expiresAt)} onChange={(e) => setV({ ...v, expiresAt: isoOrNull(e.target.value) })} /></Field>
      </div>
      <div style={two}>
        <Field label="Total uses">
          <input className="input tnum" type="number" min={1} value={v.usageLimit ?? ""} placeholder="unlimited"
            onChange={(e) => setV({ ...v, usageLimit: intOrNull(e.target.value) })} />
        </Field>
        <Field label="Uses per organisation">
          <input className="input tnum" type="number" min={1} value={v.perOrgLimit ?? ""} placeholder="unlimited"
            onChange={(e) => setV({ ...v, perOrgLimit: intOrNull(e.target.value) })} />
        </Field>
      </div>
      <Field label="Applies to" hint="none ticked = every plan">
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          {plans.filter((p) => !p.isCustom).map((p) => (
            <label key={p.id} className="sm" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" checked={v.planIds.includes(p.id)} onChange={(e) =>
                setV({ ...v, planIds: e.target.checked ? [...v.planIds, p.id] : v.planIds.filter((x) => x !== p.id) })} />
              {p.name} <span className="mut">({p.currency})</span>
            </label>
          ))}
        </div>
      </Field>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
        <label className="sm" style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
          <Switch on={v.isActive} onToggle={() => setV({ ...v, isActive: !v.isActive })} /> Active
        </label>
        <label className="sm" style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
          <Switch on={v.firstPurchaseOnly} onToggle={() => setV({ ...v, firstPurchaseOnly: !v.firstPurchaseOnly })} /> First purchase only
        </label>
      </div>
    </Modal>
  );
}
